import { Component, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FinancialService } from '../../../../services/financial.service';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { ChartModule } from 'primeng/chart';
import { PaginatorModule } from 'primeng/paginator';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { CustomerService } from '../../../../services/customer.service';
import { ExpenseDashboardSummaryDto } from '../../../../models/expenseDashboardSummary.model';
import { DateRangeEnum } from '../../../../models/enums/dateRange.enum';
import { ExpenseTypeEnum } from '../../../../models/enums/expenseType.enum';
import { EmployeeExpenseSummaryDto } from '../../../../models/employeeExpenseSummary.model';
import { SupplierExpenseSummaryDto } from '../../../../models/supplierExpenseSummary.model';
import { CompanyExpenseSummaryDto } from '../../../../models/companyExpenseSummary.model';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';


@Component({
  selector: 'app-list-expenses',
  standalone: true,
  templateUrl: './list-expenses.component.html',
  imports: [
    CommonModule,
    FormsModule,
    InputTextModule,
    DropdownModule,
    ButtonModule,
    CardModule,
    ChartModule,
    PaginatorModule,
    TableModule,
    TagModule,
  ],
  providers: [FinancialService, CustomerService]
})
export class ListExpensesComponent implements OnDestroy {
  financials: any[] = [];
  searchTerm = '';
  currentStatus = 'todos';
  currentDateRange = DateRangeEnum.All;
  currentCustomerType = 'empresa';
  loading = false;
  hoverEdit = false;
  hoverView = false;
  
  
  financialDashboard: ExpenseDashboardSummaryDto = {
    totalExpenses: 0,
    totalValueExpenses: 0,
    totalValuePending: 0,
    totalValuePaid: 0,
    totalValueCanceled: 0,
    activeSuppliers: 0,
    activeEmployees: 0,
    activeCompanies: 0,
    totalPending: 0,
    pendingExpenses: 0,
    paidExpenses: 0,
    totalValueExpensesChartData: {
      labels: [],
      data: []
    },
    totalValuePendingChartData: {
      labels: [],
      data: []
    },
    totalValuePaidChartData: {
      labels: [],
      data: []
    },
    totalValueCanceledChartData: {
      labels: [],
      data: []
    }
  };

  
  first = 0;
  rows = 5;
  totalRecords = 0;
  currentPage = 1;

  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;

  barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: { display: false },
      tooltip: { 
        enabled: true,
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        cornerRadius: 4,
        displayColors: false,
        callbacks: {
          title: function(context: any[]) {
            return context[0].label;
          },
          label: function(context: any) {
            return 'R$ ' + context.parsed.y.toLocaleString('pt-BR', { minimumFractionDigits: 2 });
          }
        }
      }
    },
    scales: {
      x: { display: false, grid: { display: false } },
      y: { 
        display: false, 
        grid: { display: false },
        min: 0,
        max: function(context: any) {
          const values = context.chart.data.datasets[0].data;
          if (!values || values.length === 0) return 100;
          const maxValue = Math.max(...values);
          return maxValue * 1.1;
        }
      }
    },
    elements: {
      bar: { borderRadius: 4 }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    },
    layout: {
      padding: {
        top: 2,
        bottom: 2,
        left: 2,
        right: 2
      }
    }
  };

  statusOptions = [
    { label: 'Todos', value: 'todos' },
    { label: 'Pendente', value: 'pendente' },
    { label: 'Quitado', value: 'quitado' },
    { label: 'Cancelado', value: 'cancelado' }
  ];

  dateRangeOptions = [
    { label: 'Todos os períodos', value: DateRangeEnum.All },
    { label: 'Este mês', value: DateRangeEnum.ThisMonth },
    { label: 'Mês passado', value: DateRangeEnum.LastMonth },
    { label: 'Últimos 3 meses', value: DateRangeEnum.LastThreeMonths },
    { label: 'Últimos 6 meses', value: DateRangeEnum.LastSixMonths },
    { label: 'Este ano', value: DateRangeEnum.ThisYear },
    { label: 'Ano passado', value: DateRangeEnum.LastYear }
  ];

  customerTypeOptions = [
    { label: 'Empresa', value: 'empresa' },
    { label: 'Fornecedor', value: 'fornecedor' },
    { label: 'Funcionário', value: 'funcionario' }
  ];

  private chartConfig = {
    totalValueExpenses: { color: '#f97316', label: 'Valor Gasto' },
    totalValuePending: { color: '#f59e0b', label: 'Valor Pendente' },
    totalValuePaid: { color: '#10b981', label: 'Valor Pago' },
    totalValueCanceled: { color: '#ef4444', label: 'Valor Cancelado' }
  };

  constructor(
    private financialService: FinancialService,
    private customerService: CustomerService,
    private router: Router
  ) {
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      this.searchTerm = term;
      this.loadClients();
      this.loadDashboard();
    });
  }

  ngOnInit(): void {
    this.loadClients();
    this.loadDashboard();
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
  }

  loadClients(): void {
    this.loading = true;
    
    const expenseType = this.getExpenseTypeFromCustomerType(this.currentCustomerType);
    this.financialService.getFinancialExpenseSummary(this.searchTerm, this.currentStatus, this.currentPage, expenseType, undefined, this.currentDateRange ).subscribe({
      next: (data: any) => {
        this.financials = data?.data ?? [];
        this.totalRecords = data.pagination?.totalRecords ?? this.financials.length;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  onSearchChange(): void {
    this.currentPage = 1; 
    this.first = 0;
    this.searchSubject.next(this.searchTerm);
  }

  onStatusChange(): void {
    this.currentPage = 1; 
    this.first = 0;
    this.loadClients();
    this.loadDashboard();
  }

  onDateRangeChange(): void {
    this.currentPage = 1; 
    this.first = 0;
    this.loadClients();
    this.loadDashboard();
  }

  onCustomerTypeChange(): void {
    this.currentPage = 1;
    this.first = 0;
    this.loadClients();
    this.loadDashboard();
  }

  onPageChange(event: any): void {
    this.first = event.first;
    this.currentPage = Math.floor(this.first / this.rows) + 1;
    this.loadClients();
  }

  createExpense(): void {
    this.router.navigate(['/app/financial/expenses/new']);
  }

  editExpense(id: number): void {
    this.router.navigate([`/app/financial/expenses/edit/${id}`]);
  }

  goToExpenses(customer: any): void {
    const customerId = this.getCustomerId(customer);
    const expenseAccountType = this.getExpenseTypeFromCustomerType(this.currentCustomerType);
    this.router.navigate([`/app/financial/expenses/view/${customerId}/${expenseAccountType}`]);
  }

  formatCustomer(customer: any): string {
  return JSON.stringify(customer, null, 2);
}

  loadDashboard(): void {
    const expenseType = this.getExpenseTypeFromCustomerType(this.currentCustomerType);
    this.financialService.getDashboardExpenses(this.searchTerm, this.currentStatus, this.currentDateRange, expenseType).subscribe({
      next: (data) => {
        this.financialDashboard = data.data;
      },
      error: (error) => {
        console.error('Error loading dashboard summary:', error);
      }
    });
  }

  getChartData(chartType: keyof typeof this.chartConfig) {
    const config = this.chartConfig[chartType];
    const chartData = this.financialDashboard[`${chartType}ChartData` as keyof ExpenseDashboardSummaryDto] as any;
    
    return {
      labels: chartData?.labels || [],
      datasets: [
        {
          data: chartData?.data || [],
          backgroundColor: config.color,
          borderColor: config.color,
          borderWidth: 1,
          borderRadius: 4
        }
      ]
    };
  }

  
  getTotalValueExpensesChartData() {
    return this.getChartData('totalValueExpenses');
  }

  getTotalValuePendingChartData() {
    return this.getChartData('totalValuePending');
  }

  getTotalValuePaidChartData() {
    return this.getChartData('totalValuePaid');
  }

  getTotalValueCanceledChartData() {
    return this.getChartData('totalValueCanceled');
  }

  getStatusSeverity(status: string): string {
    switch (status) {
      case 'quitado':
        return 'success';
      case 'pendente':
        return 'warn';
      case 'cancelado':
        return 'danger';
      default:
        return 'info';
    }
  }

  private getExpenseTypeFromCustomerType(customerType: string): ExpenseTypeEnum {
    switch (customerType) {
      case 'fornecedor':
        return ExpenseTypeEnum.Supplier;
      case 'funcionario':
        return ExpenseTypeEnum.Employee;
      case 'empresa':
      default:
        return ExpenseTypeEnum.Company;
    }
  }

  getCustomerName(customer: any): string {
    switch (this.currentCustomerType) {
      case 'funcionario':
        return customer.fullName || 'Nome não informado';
      case 'fornecedor':
      case 'empresa':
      default:
        return customer.companyName || 'Nome não informado';
    }
  }

  getCustomerId(customer: any): number {
    switch (this.currentCustomerType) {
      case 'funcionario':
        return customer.employeeId;
      case 'fornecedor':
        return customer.supplierId;
      case 'empresa':
      default:
        return customer.companyId;
    }
  }

  getTotalExpense(customer: any): number {
    return customer.totalExpense || 0;
  }

  getExpenseCount(customer: any): number {
    return customer.expenseCount || 0;
  }
}
